import { useEffect, useState } from 'react';
import { t } from 'i18next';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { InferType } from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import {
  useEditEmergencyContactMutation,
  usePatientCareTeamEmergencyContactQuery,
} from '@common/cache';

import { addEmergencyContactSchema } from '@views/pages/Patient/components/CareTeam/components/schemas/addEmergencyContactSchema.ts';
import { formatApiError } from '@common/errors/error-formatting.ts';
import { fillAddressFormValues } from '@util/google-places';
import { paths } from '@common/types';

type IFormShape = InferType<typeof addEmergencyContactSchema>;

const initialValues: Partial<IFormShape> = {
  first_name: '',
  middle_name: '',
  last_name: '',
  street: '',
  suite: '',
  email: '',
  state: '',
  city: '',
  zip_code: '',
  country: 'USA',
  phone_number: '',
};

export const useEditEmergencyContactItem = (
  patientId: string,
  pec_id: string,
) => {
  const { mutateAsync, isPending } = useEditEmergencyContactMutation(
    patientId,
    pec_id,
  );

  const { data } = usePatientCareTeamEmergencyContactQuery(patientId, pec_id);
  const [isOpen, setIsOpen] = useState(false);
  const [isSaveChangesModalOpen, setIsSaveChangesModalOpen] = useState(false);

  const formMethods = useForm<IFormShape>({
    mode: 'onChange',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver<IFormShape, any, any>(addEmergencyContactSchema),
    defaultValues: initialValues,
  });

  const {
    handleSubmit,
    formState: { isSubmitting, isValid, isDirty },
    reset,
    setValue,
  } = formMethods;

  useEffect(() => {
    if (data) {
      reset({ ...data, ...data.address });
    }
  }, [data]);

  const relationshipOptions = [
    { value: 'Spouse', label: t('common.relationship.spouse') },
    {
      value: 'Domestic partner',
      label: t('common.relationship.domesticPartner'),
    },
    { value: 'Daughter', label: t('common.relationship.daughter') },
    { value: 'Son', label: t('common.relationship.son') },
    { value: 'Sibling', label: t('common.relationship.sibling') },
    {
      value: 'Other family member',
      label: t('common.relationship.otherFamilyMember'),
    },
    { value: 'Friend', label: t('common.relationship.friend') },
    {
      value: 'Other non-family member',
      label: t('common.relationship.otherNonFamilyMember'),
    },
  ];

  const phoneTypeOptions = [
    { value: 'Home', label: t('common.phoneType.home') },
    { value: 'Cell', label: t('common.phoneType.cell') },
    { value: 'Business', label: t('common.phoneType.business') },
  ];

  const handleClose = () => {
    if (isDirty) {
      setIsSaveChangesModalOpen(true);
    } else {
      setIsOpen(false);
    }
  };

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleSelectLocation = (place: Partial<google.maps.places.Place>) => {
    fillAddressFormValues({ setValue, place });
  };

  const submitHandler = async (values: IFormShape) => {
    try {
      await mutateAsync({
        first_name: values.first_name,
        middle_name: values.middle_name,
        last_name: values.last_name,
        relationship:
          values.relationship as paths['/patient/{patient_id}/emergency-contact/{pec_id}']['patch']['requestBody']['content']['application/json']['relationship'],
        phone_number: values.phone_number,
        phone_type:
          values.phone_type as paths['/patient/{patient_id}/emergency-contact/{pec_id}']['patch']['requestBody']['content']['application/json']['phone_type'],
        state: values.state || null,
        city: values.city || null,
        zip_code: values.zip_code || null,
        country: values.country || null,
        street: values.street || null,
        email: values.email,
        suite: values.suite,
      });
      setIsSaveChangesModalOpen(false);
      setIsOpen(false);
      reset(values);
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const onSubmit = handleSubmit(submitHandler);

  const handleCloseSaveChangesModal = () => {
    setIsSaveChangesModalOpen(false);
  };

  const handleDiscardChanges = () => {
    setIsSaveChangesModalOpen(false);
    setIsOpen(false);
    reset();
  };

  const handleSaveChanges = async () => {
    await onSubmit();
  };

  return {
    isOpen,
    handleClose,
    handleOpen,
    isSubmitting,
    relationshipOptions,
    phoneTypeOptions,
    formMethods,
    onSubmit,
    isValid,
    handleSelectLocation,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleSaveChanges,
    handleDiscardChanges,
    isEditSubmitting: isPending,
  };
};
